// ============================================================================
// Moving a new-account card between stages — ONE copy of the screen's rule,
// used by the card's footer (sop-detail.tsx) and by Ask AI's new-account card
// (item #944, the new-accounts part). Moved out of the page unchanged.
//
// - The HARD gate (AFB-0081 / W-03): blockedStageMoveReason — the same test
//   runs as a database trigger.
// - Forward one stage at a time; backward moves are always allowed.
// - The checklist gate is SOFT (Ben, 2026-06-12): an unfinished checklist
//   warns and the person may go on; the account stays open and flagged.
// ============================================================================
import type { NewAccountSopActiveStage, NewAccountSopStage } from '../../../shared/types';
import { NEW_ACCOUNT_SOP_STAGES, NEW_ACCOUNT_SOP_STAGE_LABELS } from '../../../shared/types';
import { blockedStageMoveReason, type IntakeRow } from '../intake-answers';

export interface ChecklistItem {
  key: string;
  label: string;
  checked: boolean;
  checked_at: string | null;
  checked_by: string | null;
}
export type ChecklistJson = Record<string, ChecklistItem[]>;

// Which top-level checklist keys belong to which DB stage. Drives the
// auto-promote prompt and the "stage X of 7" affordance inside the
// Checklist tab. Built from the canonical 7-stage JSONB the
// nas_derive_checklist RPC produces.
export const CHECKLIST_STAGE_TO_DB_STAGE: Record<string, NewAccountSopActiveStage> = {
  stage_1_bdd: 'Info_and_Sub_Checklist',
  stage_2_am_sub_checklist: 'Info_and_Sub_Checklist',
  stage_3_antonela_billing: 'Info_and_Sub_Checklist',
  stage_4_centralized_call: 'Centralized_Confirmation_Call',
  stage_5_exec_review: 'Executive_Review',
  stage_6_qbo_setup: 'Final_Pre_Invoicing_Check',
  stage_7_final_check: 'Final_Pre_Invoicing_Check',
};

// DB stage → next active stage. Used by the auto-promote prompt and the
// Move-to-stage footer.
export const STAGE_PROGRESSION: Record<NewAccountSopActiveStage, NewAccountSopActiveStage | null> = {
  Info_and_Sub_Checklist: 'Centralized_Confirmation_Call',
  Centralized_Confirmation_Call: 'Executive_Review',
  Executive_Review: 'Final_Pre_Invoicing_Check',
  Final_Pre_Invoicing_Check: 'Completed',
  Completed: null,
};

export function stagesForDbStage(s: NewAccountSopActiveStage): string[] {
  return Object.entries(CHECKLIST_STAGE_TO_DB_STAGE)
    .filter(([, db]) => db === s)
    .map(([k]) => k);
}

export function isClusterComplete(checklist: ChecklistJson, dbStage: NewAccountSopActiveStage): boolean {
  const clusters = stagesForDbStage(dbStage);
  if (clusters.length === 0) return true;
  return clusters.every((sk) => {
    const items = checklist[sk] ?? [];
    return items.length > 0 && items.every((i) => i.checked);
  });
}

export function checklistOf(row: { checklist?: unknown }): ChecklistJson {
  if (!row.checklist || typeof row.checklist !== 'object') return {};
  return row.checklist as ChecklistJson;
}

export interface StageMoveCheck {
  /** The move is refused, in the screen's words. */
  blocked: string | null;
  /** The move may go on, but the screen asks first (the soft checklist gate). */
  warn: string | null;
}

/** What the footer's "Move to …" does for this card and this stage: refuse, warn, or go. */
export function stageMoveCheck(row: IntakeRow & { stage?: string | null; checklist?: unknown }, next: NewAccountSopStage): StageMoveCheck {
  const blocked = blockedStageMoveReason(row, next);
  if (blocked) return { blocked, warn: null };
  const stages = NEW_ACCOUNT_SOP_STAGES;
  const fromIdx = stages.indexOf(row.stage as NewAccountSopActiveStage);
  const toIdx = stages.indexOf(next as NewAccountSopActiveStage);
  // Backward moves and off-board are always allowed. Forward moves are gated.
  if (fromIdx !== -1 && toIdx !== -1 && toIdx > fromIdx) {
    if (toIdx > fromIdx + 1) return { blocked: 'Advance one stage at a time — finish the stages in order.', warn: null };
    if (!isClusterComplete(checklistOf(row), row.stage as NewAccountSopActiveStage)) {
      return {
        blocked: null,
        warn:
          `Some items in the ${NEW_ACCOUNT_SOP_STAGE_LABELS[row.stage as NewAccountSopStage]} checklist aren't done yet.\n\n` +
          `Move to ${NEW_ACCOUNT_SOP_STAGE_LABELS[next]} anyway? ` +
          `The account stays open and flagged so the missing items can be finished later.`,
      };
    }
  }
  return { blocked: null, warn: null };
}
